'use client'

import Link from 'next/link'
import { useState } from 'react'

interface FAQItem {
  question: string
  answer: string | string[] | JSX.Element
}

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  const faqs: FAQItem[] = [
    {
      question: 'How do I activate Notion AI?',
      answer: (
        <>
          Notion AI is available to all users. You can prompt AI using your
          space bar, highlighting text and selecting {`"Ask AI"`}, or via slash
          commands. You must take action for AI to activate.
          <br />
          <br />
          It will not generate content or send requests to our AI providers
          unless you perform one of the actions above. You are in control.
        </>
      )
    },
    {
      question: 'What are the key features of the service?',
      answer: (
        <>
          <p>
            Notion certainly isn`t the only AI-powered tool out there. But
            here`s what makes Notion AI special:
          </p>
          <br />
          <ul>
            <li>
              Artificial intelligence becomes more useful when it`s integrated
              across the place where you`re already doing your work, storing
              notes & docs, and collaborating with others. No need to switch
              back and forth between your notes and a separate AI-powered tool.
            </li>
            <br />
            <li>
              Compared to other tools, Notion has a unique and highly flexible
              drag-and-drop text editor, where any AI-generated content can be
              easily rearranged and transformed. Over time, Notion AI will be
              able to take advantage of even more Notion features.
            </li>
            <br />
            <li>
              Today, Notion AI has many uses across your notes and docs. But
              Notion can do a lot more than just notes — we`ll continue to
              expand Notion AI`s capabilities to help with project management
              and your team knowledge base in the future. This is just the
              beginning!
            </li>
          </ul>
        </>
      )
    },
    {
      question: 'How does Notion AI use my data?',
      answer: (
        <>
          <p>
            We follow our standard{' '}
            <Link href="/">data protection practices</Link> to keep your data
            encrypted and private.
          </p>
          <br />
          <p>
            The Notion AI Writing Suite will not use your data to train our
            models unless you opt in to a request to share your data. Any
            information used to power Notion AI will be shared with our partners
            for the sole purpose of providing you with the Notion AI features.
          </p>
          <br />
          <p>
            For more information, see the
            <Link href="/">Notion AI Supplementary Terms.</Link>
          </p>
        </>
      )
    },
    {
      question: 'How much usage is included with the Notion AI add-on plan?',
      answer: (
        <>
          <p>
            When you purchase the Notion AI add-on, you`re enabling everyone in
            your workspace (except for guests) to have unlimited use of Notion
            AI.
          </p>
          <br />
          <p>
            To ensure optimal performance and <Link href="/">fair usage</Link>{' '}
            across all Notion AI users, your access to AI features can be
            reduced depending on your usage.
          </p>
          <br />
        </>
      )
    },
    {
      question: 'Is the free trial really free?',
      answer: (
        <>
          <p>
            Yes, you get access to all premium features for 7 days with no
            strings attached.
          </p>
        </>
      )
    }
  ]

  return (
    <div className="">
      <h1 className="font-bold text-[45px] leading-[49px] tracking-[-1.22] text-[#050505] mt-5">
        Questions & answers
      </h1>
      <div className="max-w-full my-10">
        {faqs.map((faq, index) => (
          <div key={index} className="border-b border-gray-300 py-4">
            <button
              onClick={() => toggleFAQ(index)}
              className="flex justify-between items-center w-full text-left focus:outline-none"
            >
              <span className="font-medium text-lg">{faq.question}</span>
              <span className="hover:bg-gray-300 py-1 px-2 leading-[24px]">
                <span
                  className={`inline-block text-[15px] transition-transform duration-300 ${
                    openIndex === index ? 'rotate-45' : ''
                  }`}
                >
                  +
                </span>
              </span>
            </button>
            {openIndex === index && (
              <div className="mt-2 text-[#121212] font-inter text-[16px] font-[400] leading-[1.4375] tracking-[0.0013em] faq">
                {typeof faq.answer === 'string' ? (
                  <p>{faq.answer}</p>
                ) : Array.isArray(faq.answer) ? (
                  <ul className="">
                    {faq.answer.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  faq.answer
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default FAQ
